/**
 * 🟦 V18.1 — LyonHelpInfoModal.
 *
 * Modal informativo "¿Qué es Ayuda con Lyon-IA?" que explica al usuario
 * qué puede hacer conversando con Lyon-IA (disponible para Gratis +
 * Premium) y qué puede hacer dentro del Builder.
 *
 * El contenido del texto fue dictado por Marcelo (feb-2026) y debe
 * mostrarse SIN modificaciones (excepto la traducción al inglés).
 *
 * 🟦 2026-06-27 — i18n bilingüe ES/EN via `useLanguage()`.
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowUp, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LyonHelpInfoModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const es = language === 'es';
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const scrollToTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return createPortal(
    <div
      data-testid="lyon-help-info-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      className="fixed inset-0 z-[10001] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lyon-help-info-title"
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 border border-indigo-500/40 rounded-2xl shadow-2xl shadow-indigo-500/20 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-indigo-500/30 bg-gradient-to-r from-indigo-900/40 to-fuchsia-900/30 shrink-0">
          <h2
            id="lyon-help-info-title"
            className="text-base sm:text-lg font-bold text-white flex items-center gap-2"
          >
            <Sparkles className="w-5 h-5 text-fuchsia-300" />
            {es ? '¿Qué es Ayuda con Lyon-IA?' : 'What is Lyon-IA Help?'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="lyon-help-info-close"
            aria-label={es ? 'Cerrar' : 'Close'}
            className="p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800/80 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content */}
        <div
          ref={scrollRef}
          data-testid="lyon-help-info-scroll"
          className="flex-1 overflow-y-auto px-5 sm:px-7 py-6 text-slate-200 leading-relaxed text-sm sm:text-[15px] space-y-5"
        >
          <section>
            <h3 className="text-lg sm:text-xl font-bold text-fuchsia-300 mb-2">
              {es ? '🧠 ¿Qué es Lyon‑IA?' : '🧠 What is Lyon‑IA?'}
            </h3>
            <p>{es ? 'Lyon‑IA es tu asistente inteligente dentro de DamyxIA.' : 'Lyon‑IA is your intelligent assistant inside DamyxIA.'}</p>
            <p>{es
              ? 'Podés hablarle como a una persona, hacerle preguntas, pedirle ideas y también pedirle que te ayude a construir y mejorar tus apps dentro del Builder.'
              : 'You can talk to it like a person, ask questions, request ideas and also ask for help building and improving your apps inside the Builder.'}
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-1.5 text-slate-300">
              <li>{es ? 'Disponible para todos los usuarios: Plan Gratis y Plan Premium.' : 'Available for all users: Free Plan and Premium Plan.'}</li>
              <li>{es ? 'Podés usarlo para pensar, planear, escribir, corregir y mejorar lo que estás creando.' : 'You can use it to think, plan, write, correct and improve what you are creating.'}</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg sm:text-xl font-bold text-cyan-300 mb-2">
              {es ? '💬 ¿Qué puedo hacer conversando con Lyon‑IA?' : '💬 What can I do by chatting with Lyon‑IA?'}
            </h3>
            <p>{es ? 'Con solo escribirle un mensaje, Lyon‑IA puede ayudarte a:' : 'By simply writing a message, Lyon‑IA can help you to:'}</p>

            <div className="mt-3 space-y-3">
              <div>
                <p className="font-semibold text-white">{es ? 'Generar ideas de contenido' : 'Generate content ideas'}</p>
                <ul className="list-disc pl-6 mt-1 space-y-1 text-slate-300">
                  <li>{es ? 'Textos para secciones de tu landing (hero, beneficios, testimonios, FAQ, etc.).' : 'Copy for your landing sections (hero, benefits, testimonials, FAQ, etc.).'}</li>
                  <li>{es ? 'Mensajes para CTAs, slogans, descripciones de producto o servicio.' : 'Messages for CTAs, slogans, product or service descriptions.'}</li>
                  <li>{es ? 'Ideas de estructura para una página completa.' : 'Structure ideas for a complete page.'}</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-white">{es ? 'Mejorar textos que ya tenés' : 'Improve copy you already have'}</p>
                <ul className="list-disc pl-6 mt-1 space-y-1 text-slate-300">
                  <li>{es ? 'Reescribir textos para que suenen más profesionales, claros o persuasivos.' : 'Rewrite copy to sound more professional, clear or persuasive.'}</li>
                  <li>{es ? 'Adaptar el tono (más formal, más cercano, más técnico, más simple).' : 'Adapt the tone (more formal, friendly, technical, simpler).'}</li>
                  <li>{es ? 'Corregir ortografía, gramática y redacción.' : 'Fix spelling, grammar and wording.'}</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-white">{es ? 'Explicar conceptos y darte guía' : 'Explain concepts and guide you'}</p>
                <ul className="list-disc pl-6 mt-1 space-y-1 text-slate-300">
                  <li>{es ? 'Explicarte qué es una landing, un funnel, un CTA, un layout, etc.' : 'Explain what a landing, funnel, CTA, layout, etc. is.'}</li>
                  <li>{es ? 'Sugerirte buenas prácticas de diseño y contenido.' : 'Suggest design and content best practices.'}</li>
                  <li>{es ? 'Ayudarte a entender qué sección podrías agregar o mejorar.' : 'Help you understand which section you could add or improve.'}</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-white">{es ? 'Pedir ejemplos concretos' : 'Ask for concrete examples'}</p>
                <ul className="list-disc pl-6 mt-1 space-y-1 text-slate-300">
                  <li>{es ? '“Dame 3 ejemplos de titulares para mi landing de SaaS.”' : '“Give me 3 headline examples for my SaaS landing.”'}</li>
                  <li>{es ? '“Escríbeme un texto corto para la sección de beneficios.”' : '“Write a short copy for the benefits section.”'}</li>
                  <li>{es ? '“Ayúdame a explicar mi servicio en una frase clara.”' : '“Help me explain my service in one clear sentence.”'}</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-lg sm:text-xl font-bold text-amber-300 mb-2">
              {es ? '🛠️ ¿Qué puedo hacer con Lyon‑IA dentro del Builder?' : '🛠️ What can I do with Lyon‑IA inside the Builder?'}
            </h3>
            <p>{es ? 'Cuando estás trabajando en el Builder, Lyon‑IA puede ayudarte a:' : 'When you are working in the Builder, Lyon‑IA can help you:'}</p>

            <div className="mt-3 space-y-3">
              <div>
                <p className="font-semibold text-white">{es ? 'Diseñar la estructura de una página' : 'Design a page structure'}</p>
                <ul className="list-disc pl-6 mt-1 space-y-1 text-slate-300">
                  <li>{es ? 'Sugerir qué secciones incluir (hero, features, pricing, testimonios, FAQ, contacto, etc.).' : 'Suggest which sections to include (hero, features, pricing, testimonials, FAQ, contact, etc.).'}</li>
                  <li>{es ? 'Proponer el orden de las secciones para que tenga sentido para el usuario.' : 'Propose section order so it makes sense for the user.'}</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-white">{es ? 'Crear contenido para cada sección' : 'Create content for each section'}</p>
                <ul className="list-disc pl-6 mt-1 space-y-1 text-slate-300">
                  <li>{es ? 'Textos para títulos, subtítulos y párrafos.' : 'Copy for titles, subtitles and paragraphs.'}</li>
                  <li>{es ? 'Bullets de beneficios, características o pasos.' : 'Bullets for benefits, features or steps.'}</li>
                  <li>{es ? 'Llamadas a la acción claras y directas.' : 'Clear, direct calls to action.'}</li>
                </ul>
              </div>
              <div>
                <p className="font-semibold text-white">{es ? 'Inspirarte en el diseño' : 'Get design inspiration'}</p>
                <ul className="list-disc pl-6 mt-1 space-y-1 text-slate-300">
                  <li>{es ? 'Ideas de estilos (minimalista, moderno, brutalista, colorido, sobrio, etc.).' : 'Style ideas (minimalist, modern, brutalist, colorful, sober, etc.).'}</li>
                  <li>{es ? 'Sugerencias de qué tipo de imágenes o íconos usar.' : 'Suggestions on what kind of images or icons to use.'}</li>
                  <li>{es ? 'Recomendaciones de cómo organizar la información para que sea fácil de leer.' : 'Recommendations on how to organize information so it is easy to read.'}</li>
                </ul>
              </div>
            </div>

            <p className="mt-4 text-slate-400 italic text-[13px] border-l-2 border-amber-500/50 pl-3">
              {es
                ? 'Aunque algunas acciones avanzadas del Builder son exclusivas del Plan Premium, cualquier usuario puede conversar con Lyon‑IA y pedirle ayuda, ideas y textos.'
                : 'Although some advanced Builder actions are exclusive to the Premium Plan, any user can chat with Lyon‑IA and ask for help, ideas and copy.'}
            </p>
          </section>

          <section>
            <h3 className="text-lg sm:text-xl font-bold text-emerald-300 mb-2">
              {es ? '🧩 Ejemplos de cosas que podés pedirle a Lyon‑IA' : '🧩 Examples of things you can ask Lyon‑IA'}
            </h3>
            <ul className="list-disc pl-6 space-y-1.5 text-slate-300">
              <li>{es ? '“Ayúdame a escribir el texto principal de mi landing para un SaaS de facturación.”' : '“Help me write the main copy of my landing for a billing SaaS.”'}</li>
              <li>{es ? '“Dame 5 ideas de beneficios para mi app de reservas online.”' : '“Give me 5 benefit ideas for my online booking app.”'}</li>
              <li>{es ? '“Reescribí este texto para que suene más profesional: [pegar texto].”' : '“Rewrite this copy to sound more professional: [paste text].”'}</li>
              <li>{es ? '“Explícame qué secciones debería tener una landing para vender un curso online.”' : '“Explain which sections a landing to sell an online course should have.”'}</li>
              <li>{es ? '“Dame ejemplos de preguntas para una sección de FAQ.”' : '“Give me question examples for a FAQ section.”'}</li>
              <li>{es ? '“Ayúdame a resumir este párrafo en una frase corta y clara.”' : '“Help me summarize this paragraph in one short, clear sentence.”'}</li>
              <li>{es ? '“Quiero que mi página se sienta moderna y simple, ¿qué tipo de textos debería usar?”' : '“I want my page to feel modern and simple, what kind of copy should I use?”'}</li>
            </ul>
          </section>

          <section>
            <h3 className="text-lg sm:text-xl font-bold text-pink-300 mb-2">
              {es ? '📌 Cómo aprovechar mejor a Lyon‑IA' : '📌 How to get the most out of Lyon‑IA'}
            </h3>
            <ul className="list-disc pl-6 space-y-1.5 text-slate-300">
              <li><span className="text-white font-semibold">{es ? 'Sé específico:' : 'Be specific:'}</span> {es ? 'cuanto más claro seas, mejores serán las respuestas.' : 'the clearer you are, the better the answers.'}</li>
              <li><span className="text-white font-semibold">{es ? 'Dale contexto:' : 'Give it context:'}</span> {es ? 'contale qué estás creando (SaaS, curso, tienda, servicio, etc.).' : 'tell it what you are building (SaaS, course, store, service, etc.).'}</li>
              <li><span className="text-white font-semibold">{es ? 'Iterá:' : 'Iterate:'}</span> {es ? 'podés decirle “hazlo más corto”, “hazlo más informal”, “dame otra versión”.' : 'you can tell it “make it shorter”, “make it more informal”, “give me another version”.'}</li>
              <li><span className="text-white font-semibold">{es ? 'Usalo como compañero de trabajo:' : 'Use it as a coworker:'}</span> {es ? 'no solo para textos, también para ideas, estructura y claridad.' : 'not just for copy — also for ideas, structure and clarity.'}</li>
            </ul>
          </section>

          <section className="pb-2">
            <h3 className="text-lg sm:text-xl font-bold text-indigo-300 mb-2">
              {es ? '✅ Resumen' : '✅ Summary'}
            </h3>
            <ul className="list-disc pl-6 space-y-1.5 text-slate-300">
              <li>{es ? 'Podés hablar con Lyon‑IA siempre, tengas Plan Gratis o Plan Premium.' : 'You can always talk to Lyon‑IA, whether you have the Free or Premium plan.'}</li>
              <li>{es ? 'Lyon‑IA te ayuda a pensar, escribir, mejorar y estructurar tus páginas.' : 'Lyon‑IA helps you think, write, improve and structure your pages.'}</li>
              <li>{es ? 'Es ideal para crear contenido, mejorar textos y entender qué hacer dentro de DamyxIA , tanto en el Workspace como en el Builder.' : 'It is ideal for creating content, improving copy and understanding what to do inside DamyxIA, both in the Workspace and the Builder.'}</li>
              <li>{es ? 'Puedes Usarlo como tu asistente creativo y estratégico mientras creas tus trabajos o apps en DamyxIA.' : 'You can use it as your creative and strategic assistant while you create your work or apps in DamyxIA.'}</li>
            </ul>
          </section>

          {/* Botón "Volver arriba" al final del contenido */}
          <div className="pt-4 pb-2 flex justify-center">
            <button
              type="button"
              onClick={scrollToTop}
              data-testid="lyon-help-info-back-to-top"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:from-indigo-500 hover:to-fuchsia-500 text-white text-sm font-semibold shadow-lg shadow-fuchsia-500/30 transition-all hover:scale-105"
            >
              <ArrowUp className="w-4 h-4" />
              {es ? 'Volver al principio' : 'Back to top'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default LyonHelpInfoModal;
